import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { PagerdutyAiComponents } from "@p0security/integrations-pagerduty-ai-shared/types";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { CommandDisplay, Typography } from "@p0security/ui";
import type React from "react";

import {
  type InstallContext,
  awsStoreClientSecret,
  awsTf,
  gcpStoreClientSecret,
  gcpTf,
} from "./iac.ts";

type SecretManagerConfig = ItemConfigOf<
  PagerdutyAiComponents["credential"]
>["secretManager"];

type SecretManagerType = SecretManagerConfig["type"];

type InstallCommands = ReturnType<
  NonNullable<
    NonNullable<
      FrontendInstaller<PagerdutyAiComponents>["components"]["credential"]
    >["instructions"]
  >
>;

/** What a secret manager contributes to the credential install step. Methods,
 * not properties, let an implementation narrow to its own member. */
type CredentialIac<T extends SecretManagerType = SecretManagerType> = {
  instructions(
    context: InstallContext,
    account: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): InstallCommands;
  /** The command that stores the client secret, if this secret manager has one */
  storeClientSecret(
    account: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): string | undefined;
};

const gcpSmIac: CredentialIac = {
  instructions: (context, account, secretManager) => ({
    commands: {
      iac: [
        {
          header:
            'Apply the following terraform to deploy P0\'s PagerDuty connector, then click "Next".',
          command: gcpTf(context, account, secretManager),
        },
      ],
    },
  }),
  storeClientSecret: gcpStoreClientSecret,
} satisfies CredentialIac<"gcp-sm">;

const awsSmIac: CredentialIac = {
  instructions: (context, account, secretManager) => ({
    commands: {
      iac: [
        {
          header:
            'Apply the following terraform to deploy P0\'s PagerDuty connector, then click "Next".',
          command: awsTf(context, account, secretManager),
        },
      ],
    },
  }),
  storeClientSecret: awsStoreClientSecret,
} satisfies CredentialIac<"aws-sm">;

// The Azure Key Vault option is hidden, and the backend refuses it, until the
// connector runs on Azure.
const azureKvIac: CredentialIac = {
  instructions: () => ({ commands: {} }),
  storeClientSecret: () => undefined,
} satisfies CredentialIac<"azure-kv">;

const credentialIac: Record<SecretManagerType, CredentialIac> = {
  "aws-sm": awsSmIac,
  "azure-kv": azureKvIac,
  "gcp-sm": gcpSmIac,
};

const { Paragraph } = Typography;

const AppSetup: React.FC<{
  id: string;
  secretManager: SecretManagerConfig;
}> = ({ id, secretManager }) => {
  const storeClientSecret = credentialIac[secretManager.type].storeClientSecret(
    id,
    secretManager
  );
  return (
    <div>
      <Paragraph type="secondary">
        In PagerDuty, create an app for P0 under Integrations &gt; App
        Registration. Give it OAuth 2.0 functionality of type Scoped OAuth.
      </Paragraph>
      <Paragraph type="secondary">
        Give the app only the scopes that machines may request, plus{" "}
        <code>users.read</code>, which the PagerDuty MCP server requires.
        P0&apos;s tokens can hold no scope that the app lacks.
      </Paragraph>
      {storeClientSecret && (
        <>
          <Paragraph>Client secret</Paragraph>
          <Paragraph type="secondary">
            Set <code>PD_CLIENT_SECRET</code> to the app&apos;s client secret,
            and run this command to update the secret to it. The connector
            cannot request tokens until the secret holds the client secret.
          </Paragraph>
          <CommandDisplay commands={storeClientSecret} />
        </>
      )}
      <Paragraph>Client ID</Paragraph>
    </div>
  );
};

export const pagerdutyAiInstaller: FrontendInstaller<PagerdutyAiComponents> = {
  components: {
    credential: {
      instructions: (context, id, item) =>
        credentialIac[item.secretManager.type].instructions(
          context,
          id,
          item.secretManager
        ),
      items: {
        clientId: {
          labeler: (_, id, { secretManager, state }) =>
            state === "configure" && (
              <AppSetup id={id} secretManager={secretManager} />
            ),
        },
        secretManager: {
          options: {
            "aws-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "aws-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
            "gcp-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "gcp-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
          },
        },
      },
    },
  },
};
